import * as React from "react";
import axios from "axios";
import { useAccount } from "../../Account";
import { useLanguage } from "../../Language";
import {
  Await,
  defer,
  LoaderFunctionArgs,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import useBackToLink from "../resource/useBackToLink";
import { UpdateDigitalLoansButton } from "./UpdateDigitalLoansButton";
import {
  Alert,
  Box,
  Divider,
  Pagination,
  SelectChangeEvent,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { LoanItem } from "./LoanItem";
import {
  isLoansSortType,
  LoansSortControl,
  LoansSortType,
} from "./HostSystemItemSortControl";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { LSResultItemSkeleton } from "../search/ls/LSResultItem/LSResultItemSkeleton";
import { getValidatedPage } from "./getValidatedPage";
import { GetCurrrentLoansReturn } from "@tlc/ls2pac/dto/GetCurrentLoansReturn";
import { LoaderData } from "./MyAccount";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";

export interface ChildLoansLoaderData {
  id: string;
  page: number;
  sort: string;
  pageSize: number;
  totalLoans: number;
  totalLoansOverdue: number;
  loans: HostSystemItemView[];
}

export async function childLoansLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const pageSize: number = 10;
  const id = params.id!;
  const searchParams = new URL(request.url).searchParams;
  const page = getValidatedPage(searchParams.get("page"));
  const sortParam = searchParams.get("sort");
  const sort = isLoansSortType(sortParam) ? sortParam : LoansSortType[0];

  const dataPromise = axios
    .get<GetCurrrentLoansReturn>(
      `/loans/child/${id}/${page * pageSize - pageSize}/${pageSize}/${sort}`
    )
    .then(({ data }) => ({
      id,
      page,
      sort,
      pageSize,
      totalLoans: data.totalLoans,
      totalLoansOverdue: data.totalLoansOverdue,
      loans: data.loans,
    }))
    .catch((error) => {
      console.error("Error fetching loans", error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Response("Forbidden", { status: 403 });
      }
      throw error;
    });

  return defer({ data: dataPromise });
}

export const ChildLoansView: React.FC = () => {
  const { data } = useLoaderData() as { data: Promise<ChildLoansLoaderData> };
  const { patron } = useAccount();
  const { translate } = useLanguage();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  useBackToLink(translate("loans"), location);

  return (
    <React.Suspense
      fallback={
        <>
          {Array.from({ length: 10 }).map((_, i) => (
            <LSResultItemSkeleton key={i} hideActions hideCheckbox />
          ))}
        </>
      }
    >
      <Await resolve={data}>
        {(resolved) => {
          const {
            id,
            page,
            sort,
            pageSize,
            totalLoans,
            totalLoansOverdue,
            loans,
          } = resolved;

          const childId = parseInt(id);
          const child = (patron?.children || []).find((c) => c.id === childId);
          const pages = totalLoans ? Math.ceil(totalLoans / pageSize) : 0;

          const handlePageChange = (
            _e: React.ChangeEvent<unknown>,
            newPage: number
          ) => {
            searchParams.set("page", newPage.toString());
            setSearchParams(searchParams);
          };

          const handleSortChange = (event: SelectChangeEvent) => {
            const newSort = event.target.value as HostSystemItemSortType;
            searchParams.set("page", "1");
            searchParams.set("sort", newSort);
            setSearchParams(searchParams);
          };

          return (
            <>
              {totalLoans === 0 ? (
                <Alert
                  sx={{ mt: 2 }}
                  severity="info"
                  action={
                    <UpdateDigitalLoansButton patronId={child?.barcode} />
                  }
                >
                  {translate("studentNoLoansMessage")}
                </Alert>
              ) : (
                <>
                  {totalLoansOverdue > 0 && (
                    <Alert sx={{ mt: 2, mb: 1 }} severity="error">
                      {totalLoansOverdue} {translate("itemsOverdue")}
                    </Alert>
                  )}
                  <Toolbar>
                    <Typography variant="h5">
                      {totalLoans} {translate("loans")}
                    </Typography>
                    <UpdateDigitalLoansButton patronId={child?.barcode} />
                  </Toolbar>
                  <Toolbar>
                    <LoansSortControl
                      value={sort}
                      onChange={handleSortChange}
                    />
                  </Toolbar>
                  <Box>
                    {loans.map((loan: HostSystemItemView) => (
                      <>
                        <Divider />
                        <LoanItem loan={loan} key={loan.id} />
                      </>
                    ))}
                  </Box>
                  {/* show pagination when requested page out of bounds */}
                  {(totalLoans > pageSize || page > pages) && (
                    <Box display="flex" justifyContent="center" mt={2}>
                      <Stack spacing={2}>
                        <Pagination
                          count={pages}
                          color="primary"
                          variant="text"
                          page={page}
                          onChange={handlePageChange}
                          sx={{
                            "& .MuiPagination-ul, .MuiPagination-ul li": {
                              // overides nav ul li borders
                              border: "none",
                            },
                          }}
                        />
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
